// Datos del profesional responsable POR TURNO (AM/PM/HS) — 113 bloque K (hoja pág. 221 del PDF oficial) y 114 bloque M (solo el
// instructivo, pág. 232: la hoja de 114, pág. 228, no imprime el cuadro).
//
// El servidor solo puede armar UN firmante: el de la sesión que firma el documento. Decisión (T6, 2026-09-23):
// - El turno que firma NO se teclea: `firmante` lo marca y su par lleva `MARCA_FIRMANTE_SERVIDOR`, que `renderizarPdf` (packages/hce-pdf)
//   reemplaza SIEMPRE por nombre + cédula + fecha/hora del firmante real. El navegador no puede poner ahí otro nombre.
// - Los otros turnos son otras personas: se capturan como campos de texto estructurados (nombres, apellidos, documento) en el orden y
//   con los rótulos del papel. Firma y sello del papel no se capturan: la firma electrónica del servidor los reemplaza para el firmante.
// - 113 (papel): primer nombre, primer apellido, segundo apellido, número de documento. 114 (instructivo): además fecha de la consulta y
//   hora (24 h), y "los dos nombres y los dos apellidos".
// - T6b (2026-09-24): el mismo mecanismo sirve para otros papeles con VARIOS profesionales en un solo documento: 016 O (INICIAL y
//   EGRESO) y 058A C (una hoja por escala: (1), (2) y (3)). Por eso el estado y `paresProfesionalPorTurno` son genéricos sobre la lista
//   de "turnos" (`EstadoProfesionalMultiple`); AM/PM/HS es solo la instancia de 113/114.
import { MARCA_FIRMANTE_SERVIDOR } from '@logiacare/hce-pdf';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

export const TURNOS_PROFESIONAL = ['am', 'pm', 'hs'] as const;
export type TurnoProfesional = (typeof TURNOS_PROFESIONAL)[number];

export type CampoProfesionalTurno = 'fecha' | 'hora' | 'primerNombre' | 'segundoNombre' | 'primerApellido' | 'segundoApellido' | 'documento';

/** Campos que el papel de 113 imprime en el bloque K, en su orden. */
export const CAMPOS_PROFESIONAL_113: readonly CampoProfesionalTurno[] = ['primerNombre', 'primerApellido', 'segundoApellido', 'documento'];
/** Campos que el instructivo de 114 lista para el bloque M, en su orden. */
export const CAMPOS_PROFESIONAL_114: readonly CampoProfesionalTurno[] = ['fecha', 'hora', 'primerNombre', 'segundoNombre', 'primerApellido', 'segundoApellido', 'documento'];

/** Campos que imprime el papel de 016 O, 019 I, 058A C…: fecha, hora, primer nombre, primer apellido, segundo apellido, documento. */
export const CAMPOS_PROFESIONAL_PAPEL: readonly CampoProfesionalTurno[] = ['fecha', 'hora', 'primerNombre', 'primerApellido', 'segundoApellido', 'documento'];

export type DatosProfesionalTurno = Readonly<Record<CampoProfesionalTurno, string>>;

/** Varios profesionales de un mismo documento, uno por clave `K` ("turno", "hoja", "inicial"/"egreso"…). */
export type EstadoProfesionalMultiple<K extends string> = { readonly firmante: K | '' } & { readonly [P in K]: DatosProfesionalTurno };

/** `firmante`: quien firma este documento (lo arma el servidor); `''` = ninguno de la lista. */
export type EstadoProfesionalPorTurno = EstadoProfesionalMultiple<TurnoProfesional>;

function datosVacios(): DatosProfesionalTurno {
  return { fecha: '', hora: '', primerNombre: '', segundoNombre: '', primerApellido: '', segundoApellido: '', documento: '' };
}

export function estadoInicialProfesionalMultiple<K extends string>(claves: readonly K[]): EstadoProfesionalMultiple<K> {
  const estado: Record<string, unknown> = { firmante: '' };
  for (const k of claves) estado[k] = datosVacios();
  return estado as EstadoProfesionalMultiple<K>;
}

export function estadoInicialProfesionalPorTurno(): EstadoProfesionalPorTurno {
  return estadoInicialProfesionalMultiple(TURNOS_PROFESIONAL);
}

/**
 * `Par[]` del bloque, en el orden del papel (AM, PM, HS; dentro de cada turno, los campos de `campos`). `prefijoI18n` cuelga
 * `turno.<am|pm|hs>` y `campo.<campo>`; el turno que firma emite un solo par con `MARCA_FIRMANTE_SERVIDOR`.
 */
export function paresProfesionalPorTurno<K extends string = TurnoProfesional>(
  t: Traducir,
  prefijoI18n: string,
  campos: readonly CampoProfesionalTurno[],
  estado: EstadoProfesionalMultiple<K>,
  turnos: readonly K[] = TURNOS_PROFESIONAL as unknown as readonly K[],
): Par[] {
  const pares: Par[] = [];
  for (const turno of turnos) {
    const etiquetaTurno = t(`${prefijoI18n}.turno.${turno}`);
    if (estado.firmante === turno) {
      pares.push({ pregunta: `${t(`${prefijoI18n}.titulo`)} — ${etiquetaTurno}`, respuesta: MARCA_FIRMANTE_SERVIDOR });
      continue;
    }
    for (const campo of campos) {
      pares.push({ pregunta: `${etiquetaTurno} — ${t(`${prefijoI18n}.campo.${campo}`)}`, respuesta: (estado[turno]?.[campo] ?? '').trim() });
    }
  }
  return pares;
}
